import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import Checkbox from 'components/Widget/CheckboxItem';
import TextInput from 'components/Widget/Input/TextInput';
import Select from 'components/Widget/Select';
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
	selectDesignSystems,
	selectIsGuest,
	selectUser,
	selectUserSettings,
} from 'redux/db/reducers';
import { setSettings, setSettingsModalVisible } from 'redux/mode/actions';
import { DarkModeBehavior } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import Modal from '..';
import * as S from './styles';

const SettingsModal = () => {
	const dispatch = useDispatch();

	const {
		darkModeBehavior,
		connectingLinesEnabled,
		figmaIntegrationEnabled,
		createOrganizationEnabled,
		designSystemsEnabled,
		designSystemCreationEnabled,
		commentEditingEnabled,
		defaultDesignSystemId,
		designSystems,
		user,
		isGuest,
	} = useSelector((r: RootReducer) => {
		const userSettings = selectUserSettings(r.db);
		return {
			darkModeBehavior: r.mode.settings.darkModeBehavior,
			connectingLinesEnabled: r.mode.settings.connectingLinesEnabled,
			figmaIntegrationEnabled: r.mode.settings.figmaIntegrationEnabled,
			createOrganizationEnabled: r.mode.settings.createOrganizationEnabled,
			designSystemsEnabled: userSettings.designSystemsEnabled,
			designSystemCreationEnabled: userSettings.designSystemCreationEnabled,
			commentEditingEnabled: userSettings.commentEditingEnabled,
			defaultDesignSystemId: userSettings.defaultDesignSystemId,
			designSystems: selectDesignSystems(r.db),
			user: selectUser(r.db),
			isGuest: selectIsGuest(r.db),
		};
	}, shallowEqual);

	const [password, setPassword] = useState('');
	const [showPasswordInput, setShowPasswordInput] = useState(false);
	const [dsCreationPassword, setDsCreationPassword] = useState('');
	const [showDsCreationPasswordInput, setShowDsCreationPasswordInput] = useState(false);
	const [commentEditingPassword, setCommentEditingPassword] = useState('');
	const [showCommentEditingPasswordInput, setShowCommentEditingPasswordInput] = useState(false);

	const close = useCallback(() => {
		dispatch(setSettingsModalVisible(false));
		setShowPasswordInput(false);
		setPassword('');
		setShowDsCreationPasswordInput(false);
		setDsCreationPassword('');
		setShowCommentEditingPasswordInput(false);
		setCommentEditingPassword('');
	}, [dispatch]);

	const onOrganizationsToggle = useCallback(() => {
		if (createOrganizationEnabled) {
			// Turning off - no password needed
			dispatch(setSettings({ createOrganizationEnabled: false }));
			setShowPasswordInput(false);
			setPassword('');
		} else {
			// User trying to check - show password input
			setShowPasswordInput(true);
		}
	}, [createOrganizationEnabled, dispatch]);

	const onPasswordSubmit = useCallback(() => {
		if (password === 'bauhaus') {
			dispatch(setSettings({ createOrganizationEnabled: true }));
			setPassword('');
			setShowPasswordInput(false);
		} else {
			toast.error('Incorrect password');
		}
	}, [password, dispatch]);

	const onCommentEditingToggle = useCallback(() => {
		if (commentEditingEnabled) {
			if (!user || isGuest) return;
			forge.updateUser({
				update: {
					id: user.id,
					metadata: {
						settings: {
							...user.metadata?.settings,
							commentEditingEnabled: false,
						},
					},
				},
			});
			setShowCommentEditingPasswordInput(false);
			setCommentEditingPassword('');
		} else {
			setShowCommentEditingPasswordInput(true);
		}
	}, [commentEditingEnabled, user, isGuest]);

	const onCommentEditingPasswordSubmit = useCallback(() => {
		if (commentEditingPassword !== 'bauhaus') {
			toast.error('Incorrect password');
			return;
		}
		if (!user || isGuest) return;
		forge.updateUser({
			update: {
				id: user.id,
				metadata: {
					settings: {
						...user.metadata?.settings,
						commentEditingEnabled: true,
					},
				},
			},
		});
		setCommentEditingPassword('');
		setShowCommentEditingPasswordInput(false);
	}, [commentEditingPassword, user, isGuest]);

	const handleDesignSystemsToggle = useCallback(() => {
		if (!user || isGuest) {
			toast.error('Please sign in to enable Design Systems');
			return;
		}

		forge.updateUser({
			update: {
				id: user.id,
				metadata: {
					settings: {
						...user.metadata?.settings,
						designSystemsEnabled: !designSystemsEnabled,
					},
				},
			},
		});
	}, [user, isGuest, designSystemsEnabled]);

	const onDefaultDesignSystemChange = useCallback(
		(event: any) => {
			if (!user || isGuest) return;
			const value = event.target.value || null;
			forge.updateUser({
				update: {
					id: user.id,
					metadata: {
						settings: {
							...user.metadata?.settings,
							defaultDesignSystemId: value,
						},
					},
				},
			});
		},
		[user, isGuest]
	);

	const onDsCreationToggle = useCallback(() => {
		if (designSystemCreationEnabled) {
			if (!user || isGuest) return;
			forge.updateUser({
				update: {
					id: user.id,
					metadata: {
						settings: {
							...user.metadata?.settings,
							designSystemCreationEnabled: false,
						},
					},
				},
			});
			setShowDsCreationPasswordInput(false);
			setDsCreationPassword('');
		} else {
			setShowDsCreationPasswordInput(true);
		}
	}, [designSystemCreationEnabled, user, isGuest]);

	const onDsCreationPasswordSubmit = useCallback(() => {
		if (dsCreationPassword !== 'bauhaus') {
			toast.error('Incorrect password');
			return;
		}
		if (!user || isGuest) return;
		forge.updateUser({
			update: {
				id: user.id,
				metadata: {
					settings: {
						...user.metadata?.settings,
						designSystemCreationEnabled: true,
					},
				},
			},
		});
		setDsCreationPassword('');
		setShowDsCreationPasswordInput(false);
	}, [dsCreationPassword, user, isGuest]);

	// when command , is pressed, close modal
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey && event.key === ',') {
				event.preventDefault();
				close();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [close]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderDropdown = (
		value: string,
		onChange: (value: any) => void,
		options: Array<{ value: string; label: string }>,
		label: string
	) => {
		return (
			<S.Setting>
				<S.SettingRow>
					<S.Spacer />
					<S.Choice>
						<S.DropdownContainer>
							<Select maxWidth={130} onChange={onChange} value={value}>
								{options.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</Select>
						</S.DropdownContainer>
					</S.Choice>
					<S.Label>{label}</S.Label>
				</S.SettingRow>
			</S.Setting>
		);
	};

	const renderPasswordInput = (
		value: string,
		onChange: (v: string) => void,
		onSubmit: () => void
	) => (
		<S.DescriptionRow>
			<S.Spacer />
			<S.PasswordRow>
				<S.PasswordInputContainer>
					<TextInput
						type="password"
						value={value}
						onChange={(e) => onChange(e.target.value)}
						placeholder="Enter password"
						onKeyDown={(e) => {
							if (e.key === 'Enter' && value.trim()) onSubmit();
						}}
						autoFocus
					/>
				</S.PasswordInputContainer>
				<S.EnterButton onClick={onSubmit} disabled={!value.trim()}>
					<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} />
				</S.EnterButton>
			</S.PasswordRow>
		</S.DescriptionRow>
	);

	const renderCheckbox = (
		checked: boolean,
		onToggle: () => void,
		label: string,
		disabled?: boolean,
		detailText?: string
	) => {
		const handleToggle = disabled ? () => {} : onToggle;
		return (
			<S.Setting>
				<S.SettingRow>
					<S.Spacer />
					<S.Choice>
						<S.CheckboxContainer style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
							<Checkbox width={16} height={16} checked={checked} onToggled={handleToggle} />
						</S.CheckboxContainer>
					</S.Choice>
					<S.Label
						onClick={handleToggle}
						style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
					>
						{label}
					</S.Label>
				</S.SettingRow>
				{detailText && (
					<S.DescriptionRow>
						<S.Spacer />
						<S.Description>{detailText}</S.Description>
					</S.DescriptionRow>
				)}
			</S.Setting>
		);
	};

	const renderLockedSetting = (
		checked: boolean,
		onToggle: () => void,
		label: string,
		showPasswordRow: boolean,
		passwordValue: string,
		setPasswordValue: (v: string) => void,
		onPasswordSubmit: () => void,
		disabled?: boolean
	) => {
		const handleToggle = disabled ? () => {} : onToggle;
		return (
			<S.Setting>
				<S.SettingRow>
					<S.Spacer />
					<S.Choice>
						<S.CheckboxContainer style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
							<Checkbox width={16} height={16} checked={checked} onToggled={handleToggle} />
						</S.CheckboxContainer>
					</S.Choice>
					<S.Label
						onClick={handleToggle}
						style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
					>
						{label}
					</S.Label>
				</S.SettingRow>
				{showPasswordRow && renderPasswordInput(passwordValue, setPasswordValue, onPasswordSubmit)}
			</S.Setting>
		);
	};

	return (
		<Modal title="Preferences" cancelLabel="Close" submitLabel="Save" onClose={close}>
			<S.Content>
				<S.FirstSectionHeader>This Device</S.FirstSectionHeader>
				{renderDropdown(
					darkModeBehavior,
					(event: any) =>
						dispatch(setSettings({ darkModeBehavior: event.target.value as DarkModeBehavior })),
					Object.values(DarkModeBehavior).map((behavior) => ({
						value: behavior,
						label: behavior,
					})),
					'Dark mode behavior'
				)}
				{renderCheckbox(
					connectingLinesEnabled,
					() => dispatch(setSettings({ connectingLinesEnabled: !connectingLinesEnabled })),
					'Connecting lines'
				)}
				{renderCheckbox(
					figmaIntegrationEnabled,
					() => dispatch(setSettings({ figmaIntegrationEnabled: !figmaIntegrationEnabled })),
					'Figma integration (experimental)'
				)}
				{renderLockedSetting(
					createOrganizationEnabled,
					onOrganizationsToggle,
					'Team Management (locked)',
					showPasswordInput && !createOrganizationEnabled,
					password,
					setPassword,
					onPasswordSubmit
				)}
				<S.SectionHeader>All Devices</S.SectionHeader>
				{renderCheckbox(
					designSystemsEnabled,
					handleDesignSystemsToggle,
					'Design Systems 2.0',
					isGuest,
					'Enabling this disables legacy design systems (e.g. "Design System" panel in scene)'
				)}
				{designSystemsEnabled &&
					renderDropdown(
						defaultDesignSystemId ?? '',
						onDefaultDesignSystemChange,
						[
							{ value: '', label: 'None' },
							...designSystems
								.map((ds) => ({ value: ds.id, label: ds.title }))
								.sort((a, b) => a.label.localeCompare(b.label)),
						],
						'Default design system'
					)}
				{renderLockedSetting(
					designSystemCreationEnabled,
					onDsCreationToggle,
					'Design System Creation (locked)',
					showDsCreationPasswordInput && !designSystemCreationEnabled && !isGuest,
					dsCreationPassword,
					setDsCreationPassword,
					onDsCreationPasswordSubmit,
					isGuest
				)}
				{renderLockedSetting(
					commentEditingEnabled,
					onCommentEditingToggle,
					'Comment Editing (locked)',
					showCommentEditingPasswordInput && !commentEditingEnabled && !isGuest,
					commentEditingPassword,
					setCommentEditingPassword,
					onCommentEditingPasswordSubmit,
					isGuest
				)}
			</S.Content>
		</Modal>
	);
};

export default SettingsModal;
